import { isEmptyObj, isType } from "@dhx/ts-common/core";
import { Callback, IEventSystem } from "@dhx/ts-common/events";
import { Id } from "@dhx/ts-common/types";
import { DataCollection, IDataConfig, IDataItem } from "@dhx/ts-data";
import { ItemConfig } from "..";
import { DataEvents, IDiagramConfig } from "./types";

interface IShapeCollectionConfig extends IDataConfig {
	diagramConfig: IDiagramConfig;
}

export class ShapesCollection extends DataCollection {
	public config: IShapeCollectionConfig;

	private _groupChildren: { [id: string]: IDataItem[] } = {};
	private _children: { [id: string]: IDataItem[] } = {};
	private _roots: Id[] = [];

	constructor(config: IShapeCollectionConfig, events: IEventSystem<DataEvents>) {
		super(config, events);

		this.events.on(DataEvents.change, (_, mode, item) => {
			const type = item?.$item?.getBaseType();

			if (mode === "remove") {
				this._removeNested(item);
				this._removeCore(item.$parent);

				if (type === "group" || type === "swimlane") {
					this._removeGroupChildren(item);
				}
			}
			if (mode === "add") {
				if (item.parent) {
					this._addCore({ from: item.parent, to: item.id }, -1);
				}
				if (type === "group" || type === "swimlane") {
					if (type === "swimlane") {
						this._checkSwimlaneCells(item);
					}
					this._setGroupChildren(item);
				}
			}
			if (mode === "update" && (type === "group" || type === "swimlane")) {
				this._setGroupChildren(item);
			}

			this._mark_chains();
		});
	}
	public getChildren(id: Id, isTree = true): IDataItem[] {
		const parent = this._groupChildren[id] || this._children[id];
		let children = [];
		if (parent) {
			children = [...children, ...parent];
			if (isTree) {
				for (let index = 0; index < parent.length; index++) {
					children = [...children, ...this.getChildren(parent[index].id)];
				}
			}
		}
		return children;
	}
	public eachChild(id: Id, callback: Callback, isTree = true): void {
		const parent = this._groupChildren[id] || this._children[id];

		if (!parent) {
			return;
		}

		for (let index = 0; index < parent.length; index++) {
			const item = parent[index];
			callback.call(this, item);

			if ((this._groupChildren[item.id] || this._children[item.id]) && isTree) {
				this.eachChild(item.id, callback);
			}
		}
	}
	public eachParent(id: Id, callback: Callback, self = false): void {
		const item = this.getItem(id);
		if (!item || this._roots.includes(id)) {
			return;
		}
		if (self) {
			callback.call(this, item);
		}
		const parent = this.getItem(item.parent || item.$group);
		callback.call(this, parent);
		this.eachParent(item.parent || item.$group, callback);
	}
	public getNearId(id: Id): Id {
		const item = this._pull[id];
		if (!item) {
			if (!this._order.length) {
				return "";
			}
			return this._order[0].id;
		}
		let test = item;
		const { type } = this.config.diagramConfig;

		while ((type === "org" || type === "mindmap") && test.$parent) {
			test = this._pull[this._pull[test.$parent].from];
			if (test.open === false) {
				return test.id;
			}
		}
		return item.id;
	}
	public mapVisible(handler: any) {
		const result = [];
		const { type } = this.config.diagramConfig;

		if (type === "mindmap" || type === "org") {
			const roots = this.getRoots();
			for (let i = 0; i < roots.length; i++) {
				this._eachBranch(this.getItem(roots[i]), handler, result);
			}
		} else {
			this.forEach(item => {
				if (item.hidden) {
					return;
				}
				if (item.$item.getBaseType() === "line") {
					const from = this.getItem(item.from) || ({} as any);
					const to = this.getItem(item.to) || ({} as any);

					if (from.hidden || to.hidden) {
						return;
					}

					this._setLineLevel(item, from, to);
				}
				result.push(handler(item));
			});
		}
		return result;
	}
	public getRoots(): Id[] {
		return this._roots;
	}
	public getRoot(id: Id): Id {
		if (!id || !this.exists(id)) {
			return;
		}
		const item = this.getItem(id);

		if (!item.$group && !item.parent) {
			return id;
		}

		return this.getRoot(item.$group || item.parent);
	}
	public serialize(): IDataItem[] {
		return this._serialize([...super.serialize()]) || [];
	}
	protected _serialize(data: IDataItem[]): IDataItem[] {
		if (!data.length) return;

		return data.map(item => {
			if (isType(item) === "object") {
				const newItem = { ...item };
				Object.entries(newItem).forEach(([key, value]) => {
					if (key.startsWith("$")) {
						delete newItem[key];
					} else if (Array.isArray(value)) {
						newItem[key] = this._serialize(value);
					} else if (isType(value) === "object") {
						newItem[key] = this._serialize([value])[0];
					}
				});
				return newItem;
			}
			return item;
		});
	}
	protected _removeNested(item: IDataItem): void {
		const { type } = this.config.diagramConfig;
		const { $connection, $group } = item;

		if ($group) {
			this._setGroupChildren(this.getItem($group));
		}

		if ($connection) {
			for (let i = 0; i < $connection.length; i++) {
				if (type === "mindmap" || type === "org") {
					this._removeNested(this.getItem($connection[i][1]));
					this._removeCore($connection[i][1]);
				}
				this._removeCore($connection[i][0]);
			}
		}
	}
	protected _eachBranch(item: ItemConfig, handler: any, stack: any[]): void {
		if (item.hidden) {
			return;
		}
		stack.push(handler(item)); // master item
		if (item.open !== false) {
			const kids = item.$connection;
			if (kids) {
				for (let i = 0; i < kids.length; i++) {
					const child = this.getItem(kids[i][1]);
					if (!child.hidden) {
						stack.push(handler(this.getItem(kids[i][0]))); // link
						this._eachBranch(child, handler, stack);
					}
				}
			}
		}
	}
	protected _parse_data(data: IDataItem[]): void {
		const links = [];
		let linksInData = false;

		for (let i = 0; i < data.length; i++) {
			const item = data[i];
			if (item.parent && !linksInData) {
				links.push({ from: item.parent, to: item.id });
			}
			if (item.from) {
				linksInData = true;
			}
		}
		if (links.length && !linksInData) {
			data = data.concat(links);
		}
		super._parse_data(data);

		this._mark_chains();
		this.mapVisible(item => {
			const type = item?.$item.getBaseType();

			if (type === "group" || type === "swimlane") {
				if (type === "swimlane") {
					this._checkSwimlaneCells(item);
				}
				this._setGroupChildren(item);
			}
		});
	}
	protected _mark_chains() {
		this._roots = [];
		this._children = {};

		const hash = {};
		const parents = {};
		const $parents = {};
		const $assistants = {};
		const $partners = {};

		const { type } = this.config.diagramConfig;

		this.forEach(item => {
			const itemType = item.$item.getBaseType();

			if (itemType === "line") {
				const link = item;
				parents[link.to] = link.id;

				if (!hash[link.from]) {
					hash[link.from] = [];
				}
				hash[link.from].push([item.id, link.to]);

				if (type === "default") {
					if (!hash[link.to]) {
						hash[link.to] = [];
					}
					hash[link.to].push([item.id, link.from]);
				}

				$parents[link.to] = link.from;
			}
			if (type === "org") {
				if (item.assistant) {
					if (!$assistants[item.parent]) {
						$assistants[item.parent] = [];
					}
					$assistants[item.parent].push(item.id);
				}
				if (item.partner) {
					if (!$partners[item.parent]) {
						$partners[item.parent] = [];
					}
					$partners[item.parent].push(item.id);
				}
			}
		});

		this.forEach(item => {
			const itemType = item.$item.getBaseType();
			const { type, lineGap } = this.config.diagramConfig;

			item.$typeConfig = { type, lineGap };

			if (itemType === "shape") {
				item.$connection = hash[item.id];

				if (type === "mindmap" || type === "org") {
					item.$parent = parents[item.id];
					if (!item.parent) {
						item.parent = $parents[item.id];
					}
					if ($assistants[item.id]) {
						item.$assistants = $assistants[item.id];
					} else {
						delete item.$assistants;
					}
					if ($partners[item.id]) {
						item.$partners = { common: $partners[item.id] };
					} else {
						delete item.$partners;
					}
				}
			}
			if (itemType === "shape" || itemType === "group" || itemType === "swimlane") {
				if (
					((type === "org" || type === "mindmap") && !item.parent) ||
					(type === "default" && !item.$group)
				) {
					this._roots.push(item.id);
				}
			}
			if (type === "mindmap" || type === "org") {
				if (itemType === "shape") {
					if (item.parent) {
						if (Array.isArray(this._children[item.parent])) {
							this._children[item.parent].push(item);
						} else {
							this._children[item.parent] = [item];
						}
					}
				}
			}
		});

		if (type === "mindmap" || type === "org") {
			if (type === "mindmap") {
				const root = this.getItem(this._roots[0]);
				if (!isEmptyObj(root?.openDir) && Object.values(root?.openDir).every(open => !open)) {
					root.open = false;
				}
			}

			this._setBranchLevel(this.getItem(this._roots[0]));
		}
	}
	protected _setBranchLevel(item, level = 0) {
		if (!item) return;
		const kids = item?.$connection;

		if (kids) {
			let sub;
			for (let i = 0; i < kids.length; i++) {
				sub = this.getItem(kids[i][1]);
				if (!sub.hidden) {
					item.$level = level;
					this._setBranchLevel(sub, level + 1);
				}
			}
		}
		item.$level = level++;
	}
	protected _setLineLevel(line, from, to): void {
		if (!from && !to) {
			line.$level = 0;
			return;
		}
		let level = 0;
		if (from?.$group && from?.id) {
			const parents = [];
			this.eachParent(from.id, i => i?.id && parents.push(i.id));
			if (parents.length > level) level = parents.length;
		}
		if (to?.$group && to?.id) {
			const parents = [];
			this.eachParent(to.id, i => i?.id && parents.push(i.id));
			if (parents.length > level) level = parents.length;
		}
		line.$level = level;
	}
	private _setGroupChildren(item: IDataItem): void {
		if (!item) return;

		this._groupChildren[item.id] = [];

		if (!item.groupChildren) {
			item.groupChildren = [];
		}
		if (item?.layout?.length) {
			const collection = new Set(item.layout.flat(Infinity));
			item.groupChildren = Array.from(collection);
		}

		const children = item.groupChildren;
		if (Array.isArray(children) && children.length) {
			for (let index = 0; index < children.length; index++) {
				const child = this._pull[children[index]];

				if (child) {
					child.$group = item.id;
					this._groupChildren[item.id].push(child);

					if (child.$item.getBaseType() === "group" && !this._groupChildren[child.id]) {
						this._setGroupChildren(child);
					}
				}
			}
		}
		this._setGroupVisable(item);
	}
	private _removeGroupChildren({ id }: IDataItem): void {
		if (this._groupChildren[id].length) {
			for (let index = 0; index < this._groupChildren[id].length; index++) {
				this.remove(this._groupChildren[id][index].id);
			}
		}
		delete this._groupChildren[id];
	}
	private _setGroupVisable({ id, open, header }: IDataItem): void {
		const isOpen = header?.enable && header?.closable ? open : true;

		if (!isOpen) {
			this.eachChild(id, item => this.update(item.id, { hidden: !open }, true));
		} else {
			this.eachChild(id, item => {
				if (this._pull[item.$group]?.open && !this._pull[item.$group]?.hidden) {
					this.update(item.id, { hidden: !open }, true);
				}
			});
		}
	}
	private _checkSwimlaneCells(item: IDataItem): void {
		const { layout } = item;
		const pull = {};

		for (let i = 0; i < layout.length; i++) {
			const row = layout[i];
			for (let k = 0; k < row.length; k++) {
				const cell = row[k];
				if (!pull[cell] && !this.exists(cell)) {
					pull[cell] = {
						type: "$sgroup",
						id: cell,
					};
				}
			}
		}
		this.add(Object.values(pull));
	}
}
